/**
 * Error message utilities
 * Provides reusable functions for converting technical error messages to user-friendly messages
 */

/**
 * Converts technical error messages to user-friendly generic messages
 *
 * This utility standardizes error messages across the application by:
 * - Converting HTTP error messages to user-friendly text
 * - Preserving known user-friendly messages
 * - Providing fallback messages for unknown errors
 *
 * @param error - The error message from the auth service
 * @returns User-friendly error message
 *
 * @example
 * ```typescript
 * getGenericErrorMessage('Http failure response'); // "Invalid username or password."
 * getGenericErrorMessage('Invalid username or password'); // "Invalid username or password"
 * getGenericErrorMessage(); // "Login failed. Please try again."
 * ```
 */
export function getGenericErrorMessage(error?: string): string {
  if (!error) {
    return 'Login failed. Please try again.';
  }

  // Check if it's a technical/HTTP error message
  if (error.includes('Http failure') || error.includes('401') || error.includes('403') || error.includes('500')) {
    return 'Invalid username or password.';
  }

  // If it's already a user-friendly message, keep it
  if (error === 'Invalid username or password' ||
      error === 'Login method not supported' ||
      error === 'You do not have access to Fund Management Dashboard') {
    return error;
  }

  // For any other error, use generic message
  return 'Login failed. Please try again.';
}
